<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-semibold">Matric Number Lookup Attempts</h3>
      <div class="flex items-center gap-2">
        <Select
          v-model="statusFilter"
          :options="statusOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="All Statuses"
          class="w-40"
          showClear
          data-testid="matric-status-filter"
        />
        <Button icon="pi pi-refresh" severity="secondary" text size="small" @click="fetchAttempts()" />
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <ProgressSpinner style="width: 36px; height: 36px" />
    </div>

    <div v-else-if="attempts.length === 0" class="bg-white rounded-lg shadow p-8 text-center">
      <i class="pi pi-check-circle text-4xl text-gray-300 mb-3"></i>
      <p class="text-gray-500">No matric retrieval attempts found.</p>
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="a in attempts"
        :key="a.id"
        class="bg-white rounded-lg shadow p-4"
        data-testid="matric-attempt-item"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="font-medium text-sm">{{ a.first_name }} {{ a.surname }} {{ a.other_name ?? '' }}</span>
              <Tag
                :severity="statusSeverity(a.status)"
                :value="statusLabel(a.status)"
                class="text-xs"
              />
              <Tag
                :severity="resultSeverity(a.result)"
                :value="resultLabel(a.result)"
                class="text-xs"
              />
            </div>
            <div class="text-xs text-gray-500 mt-1 space-x-3">
              <span v-if="a.email"><i class="pi pi-envelope text-xs mr-1"></i>{{ a.email }}</span>
              <span v-if="a.phone"><i class="pi pi-phone text-xs mr-1"></i>{{ a.phone }}</span>
              <span v-if="a.programme?.program_name"><i class="pi pi-book text-xs mr-1"></i>{{ a.programme.program_name }}</span>
              <span v-if="a.year_of_graduation"><i class="pi pi-calendar text-xs mr-1"></i>{{ a.year_of_graduation }}</span>
            </div>
            <div class="text-xs text-gray-400 mt-1">
              {{ formatDate(a.created_at) }}
            </div>
            <div v-if="a.matchedStudent" class="text-xs text-green-700 mt-1">
              Resolved to: {{ a.matchedStudent.first_name }} {{ a.matchedStudent.surname }} ({{ a.matchedStudent.matric_no }})
            </div>
            <div v-if="a.resolver" class="text-xs text-gray-400 mt-0.5">
              By {{ a.resolver.name }} · {{ formatDate(a.resolved_at) }}
            </div>
            <div v-if="a.admin_notes" class="text-xs text-gray-500 mt-1 italic">{{ a.admin_notes }}</div>
          </div>

          <div v-if="a.status === 'pending'" class="flex items-center gap-1 shrink-0">
            <Button
              icon="pi pi-link"
              label="Resolve"
              size="small"
              severity="success"
              :data-testid="`matric-resolve-${a.id}`"
              @click="openResolve(a)"
            />
            <Button
              icon="pi pi-times"
              size="small"
              severity="secondary"
              text
              :data-testid="`matric-dismiss-${a.id}`"
              @click="openDismiss(a)"
            />
          </div>
        </div>
      </div>

      <div v-if="paginatorInfo.lastPage > 1" class="flex justify-center pt-2">
        <Paginator
          :rows="20"
          :totalRecords="paginatorInfo.total"
          :first="(paginatorInfo.currentPage - 1) * 20"
          @page="onPage"
        />
      </div>
    </div>

    <!-- Resolve Dialog -->
    <Dialog
      :visible="resolveOpen"
      modal
      header="Resolve Matric Lookup"
      class="w-full max-w-md"
      :draggable="false"
      @update:visible="resolveOpen = $event"
    >
      <div v-if="resolving" class="space-y-3 pt-2">
        <p class="text-sm text-gray-600">
          <strong>{{ resolving.first_name }} {{ resolving.surname }}</strong> could not find their matric number.
          Search for the correct student and send them their matric number via email.
        </p>

        <div v-if="resolving.email" class="text-xs text-gray-500">
          <i class="pi pi-envelope mr-1"></i>Matric number will be emailed to <strong>{{ resolving.email }}</strong>
        </div>
        <div v-else class="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
          <i class="pi pi-exclamation-triangle mr-1"></i>No email provided — you'll need to contact the applicant manually.
        </div>

        <StudentSearchInput
          v-model="selectedStudent"
          label="Student"
          placeholder="Search by name or matric number..."
          data-testid="resolve-student-search"
        />

        <div v-if="selectedStudent" class="bg-gray-50 rounded p-3 text-sm">
          <div class="font-medium">{{ selectedStudent.first_name }} {{ selectedStudent.surname }}</div>
          <div v-if="selectedStudent.matric_no" class="text-xs text-gray-500">Matric: {{ selectedStudent.matric_no }}</div>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Admin Notes (optional)</label>
          <Textarea v-model="resolveNotes" rows="2" autoResize class="w-full text-sm" placeholder="Any notes about this resolution..." data-testid="resolve-notes" />
        </div>
      </div>

      <template #footer>
        <Button size="small" label="Cancel" severity="secondary" text @click="resolveOpen = false" />
        <Button
          size="small"
          label="Resolve & Send"
          icon="pi pi-send"
          :loading="submittingResolve"
          :disabled="submittingResolve || !selectedStudent"
          data-testid="resolve-confirm"
          @click="submitResolve"
        />
      </template>
    </Dialog>

    <!-- Dismiss Dialog -->
    <Dialog
      :visible="dismissOpen"
      modal
      header="Dismiss Lookup Attempt"
      class="w-full max-w-sm"
      :draggable="false"
      @update:visible="dismissOpen = $event"
    >
      <div class="space-y-3 pt-2">
        <p class="text-sm text-gray-600">
          Dismiss this lookup attempt? It will be marked as resolved without sending any information.
        </p>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
          <Textarea v-model="dismissNotes" rows="2" autoResize class="w-full text-sm" placeholder="e.g. Duplicate entry, invalid details..." data-testid="dismiss-notes" />
        </div>
      </div>

      <template #footer>
        <Button size="small" label="Cancel" severity="secondary" text @click="dismissOpen = false" />
        <Button
          size="small"
          label="Dismiss"
          severity="danger"
          :loading="submittingDismiss"
          :disabled="submittingDismiss"
          data-testid="dismiss-confirm"
          @click="submitDismiss"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import StudentSearchInput from '@/components/common/StudentSearchInput.vue';
import { useApolloClient } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import {
  GET_MATRIC_RETRIEVAL_ATTEMPTS,
  RESOLVE_MATRIC_RETRIEVAL_ATTEMPT,
  DISMISS_MATRIC_RETRIEVAL_ATTEMPT,
} from '@/graphql/transcript';
import type { Student } from '@/types/composition-functions';

const toast = useToast();
const { resolveClient } = useApolloClient();

interface Attempt {
  id: string;
  surname: string;
  first_name: string;
  other_name?: string | null;
  email?: string | null;
  phone?: string | null;
  programme?: { id: string; program_name: string } | null;
  year_of_graduation?: string | null;
  result: string;
  status: string;
  matchedStudent?: { id: string; matric_no: string; first_name: string; surname: string } | null;
  resolver?: { id: string; name: string } | null;
  resolved_at?: string | null;
  admin_notes?: string | null;
  created_at: string;
}

const attempts = ref<Attempt[]>([]);
const loading = ref(false);
const statusFilter = ref<string | null>(null);
const paginatorInfo = ref({ currentPage: 1, lastPage: 1, total: 0 });

const statusOptions = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Resolved', value: 'RESOLVED' },
  { label: 'Dismissed', value: 'DISMISSED' },
];

const resolveOpen = ref(false);
const dismissOpen = ref(false);
const resolving = ref<Attempt | null>(null);
const dismissing = ref<Attempt | null>(null);
const selectedStudent = ref<Student | null>(null);
const resolveNotes = ref('');
const dismissNotes = ref('');
const submittingResolve = ref(false);
const submittingDismiss = ref(false);

const fetchAttempts = async(page = 1) => {
  loading.value = true;
  try {
    const client = resolveClient();
    const { data } = await client.query({
      query: GET_MATRIC_RETRIEVAL_ATTEMPTS,
      variables: {
        page,
        first: 20,
        status: statusFilter.value || undefined,
      },
      fetchPolicy: 'network-only',
    });
    attempts.value = data?.matricRetrievalAttempts?.data ?? [];
    paginatorInfo.value = data?.matricRetrievalAttempts?.paginatorInfo ?? { currentPage: 1, lastPage: 1, total: 0 };
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Failed to load attempts.';
    toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
  } finally {
    loading.value = false;
  }
};

watch(statusFilter, () => fetchAttempts());
onMounted(() => fetchAttempts());

const onPage = (event: { page: number }) => {
  fetchAttempts(event.page + 1);
};

const openResolve = (a: Attempt) => {
  resolving.value = a;
  selectedStudent.value = null;
  resolveNotes.value = '';
  resolveOpen.value = true;
};

const openDismiss = (a: Attempt) => {
  dismissing.value = a;
  dismissNotes.value = '';
  dismissOpen.value = true;
};

const submitResolve = async() => {
  if (!resolving.value || !selectedStudent.value) return;
  submittingResolve.value = true;
  try {
    const client = resolveClient();
    await client.mutate({
      mutation: RESOLVE_MATRIC_RETRIEVAL_ATTEMPT,
      variables: {
        id: resolving.value.id,
        student_id: selectedStudent.value.id,
        admin_notes: resolveNotes.value || undefined,
      },
    });
    toast.add({
      severity: 'success',
      summary: 'Resolved',
      detail: resolving.value.email
        ? `Matric number sent to ${resolving.value.email}`
        : 'Attempt marked as resolved.',
      life: 3000,
    });
    resolveOpen.value = false;
    fetchAttempts(paginatorInfo.value.currentPage);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Failed to resolve.';
    toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
  } finally {
    submittingResolve.value = false;
  }
};

const submitDismiss = async() => {
  if (!dismissing.value) return;
  submittingDismiss.value = true;
  try {
    const client = resolveClient();
    await client.mutate({
      mutation: DISMISS_MATRIC_RETRIEVAL_ATTEMPT,
      variables: {
        id: dismissing.value.id,
        admin_notes: dismissNotes.value || undefined,
      },
    });
    toast.add({ severity: 'info', summary: 'Dismissed', life: 2000 });
    dismissOpen.value = false;
    fetchAttempts(paginatorInfo.value.currentPage);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Failed to dismiss.';
    toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
  } finally {
    submittingDismiss.value = false;
  }
};

const statusSeverity = (s: string) => {
  switch (s) {
  case 'pending': return 'warning';
  case 'resolved': return 'success';
  case 'dismissed': return 'secondary';
  default: return 'info';
  }
};

const statusLabel = (s: string) => {
  switch (s) {
  case 'pending': return 'Pending';
  case 'resolved': return 'Resolved';
  case 'dismissed': return 'Dismissed';
  default: return s;
  }
};

const resultSeverity = (r: string) => {
  switch (r) {
  case 'found': return 'success';
  case 'not_found': return 'danger';
  case 'multiple_matches': return 'warning';
  default: return 'info';
  }
};

const resultLabel = (r: string) => {
  switch (r) {
  case 'found': return 'Found';
  case 'not_found': return 'Not Found';
  case 'multiple_matches': return 'Multiple Matches';
  default: return r;
  }
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};
</script>
